<template>
  <div
    class="nav-menu-container"
    :class="{
      'is-open': menuData.isOpen,
      'is-hidden-left': menuData.isHiddenLeft,
      'nested-menu': menuData.name !== 'Home',
    }"
  >
    <!-- MENU HEADER -->
    <div class="nav-header nav-divider">
      <button
        class="header-btn"
        @click="menuData.name !== 'Home' ? $emit('close-menu') : handleLinkClick(menuData.link)"
        type="button"
      >
        <div class="nav-item-content">
          <img
            v-if="menuData.name !== 'Home'"
            class="nav-arrow back-arrow"
            src="../../../assets/sessions/common/down-arrow.png"
            alt="#"
          />
          <div class="nav-item-image-container">
            <!-- change 'false' to '!navOpen' to see hamburger menu -->
            <!-- <div v-if="false" class="burger-menu"></div> -->
            <img
              class="header-image"
              :src="menuData.navOpenImage || menuData.imageUrl"
              :name="menuData.name"
              alt="menu icon"
            />
          </div>
          <span>{{ menuData.name }}</span>
        </div>
      </button>
    </div>
    <!-- END OF MENU HEADER -->

    <!-- MENU ITEMS -->
    <ul
      class="nav-items"
      :style="{ height: `${windowHeight - 51}px` }"
      @mouseover="$emit('open-nav')"
      @focus="$emit('open-nav')"
    >
      <li
        v-for="item in menuData.items"
        :key="item.name"
        :class="{
          'settings': item.name === 'Settings',
          'is-active': isActiveSection(item),
          'nav-divider': item.divider,
          'has-no-items': !item.items,
        }"
      >
        <button
          @click="
            !!item.link
              ? handleLinkClick(item.link)
              : $emit('open-menu', {
                  menuToOpenName: item.name,
                  hideMenu: menuData.name,
                })
          "
          :data-test="`nav-item-button-${item.name}`"
          type="button"
        >
          <div class="nav-item-content">
            <div class="nav-item-image-container">
              <div
                v-if="itemHasImage(item)"
                :class="{
                  'team-image-container team-image-container-sm':
                    menuData.name === 'My Teams' && item.name !== 'Setup a team',
                }"
              >
                <img
                  class="nav-item-image"
                  :class="{
                    'circle-image':
                      menuData.name === 'Team Insights Pathway' ||
                      menuData.name === '9 Questions Team Pathways',
                  }"
                  :src="
                    sideNavIsOpen
                      ? item.navOpenImage || item.imageUrl
                      : item.navClosedImage || item.imageUrl
                  "
                  :name="item.name"
                  :data-test="`nav-item-img-${item.name}`"
                  :alt="item.name"
                />
              </div>
              <div v-else class="nav-item-bullet" />
            </div>
            <span :data-test="`nav-item-title-${item.name}`">{{ item.name }}</span>
            <img
              v-if="!!item.items"
              class="nav-arrow"
              src="../../../assets/sessions/common/down-arrow.png"
              alt="#"
            />
          </div>
        </button>
      </li>
    </ul>
    <!-- END OF MENU ITEMS -->
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { CmsResourceRelationship } from "../../../services/cms/cms-type-definitions";
import { Menu, SideNavSection } from "./side-nav.types";
import { isSectionActive, navigateTo } from "./side-nav.utils";

const props = withDefaults(
  defineProps<{
    sideNavIsOpen: boolean;
    isHomeMenu?: boolean;
    isHiddenLeft?: boolean;
    menuData: Menu;
    windowHeight: number;
  }>(),
  {
    isHomeMenu: false,
    isHiddenLeft: false,
  },
);

const activeSection = computed(() => {
  const items = props.menuData?.items as any[] | undefined;
  if (!items) {
    return null;
  }
  const result = items.reduce(
    (previousValue: { priority: number; item: any }, currentValue) => {
      const value = currentValue as any;
      const gotNewValue =
        !!value.activePaths &&
        isSectionActive(value.activePaths) &&
        typeof value.activePriority === "number" &&
        value.activePriority < previousValue.priority;
      return gotNewValue ? { priority: value.activePriority, item: currentValue } : previousValue;
    },
    { priority: 9999, item: null },
  ).item;
  return result || null;
});

function isActiveSection(item: SideNavSection): boolean {
  return item === activeSection.value;
}

function handleLinkClick(route: string | CmsResourceRelationship): void {
  if (!route) return;
  navigateTo(route);
}

function itemHasImage(item: SideNavSection): boolean {
  return !!item.imageUrl || !!item.navOpenImage || !!item.navClosedImage;
}
</script>

<style lang="less">
@import (reference) "../../../styles/bootstrap/variables.less";

.nav-menu-container {
  position: absolute;
  transition: @side-nav-long-transition;
  width: @side-nav-full-width;
  height: 100%;
  transform: initial;

  &:not(.is-open) {
    transform: translateX(@side-nav-full-width);
  }

  &.is-hidden-left {
    transform: translateX(-@side-nav-full-width);
  }

  button {
    width: 100%;
    height: 100%;
    color: #eee;
    overflow-x: hidden;

    &.header-btn {
      display: flex;
      justify-content: center;
    }
  }

  button:hover,
  .is-active button {
    color: #fff;

    .nav-arrow {
      opacity: 1;
    }

    &:not(.header-btn) {
      background-color: @side-nav-hover-bg-color;
    }
  }

  ul {
    padding: 0;
    margin: 0;
  }

  li {
    list-style: none;
  }

  .nav-header {
    height: @navbar-height + 1px;
  }

  .nav-divider {
    border-bottom: 1px solid @side-nav-hover-bg-color;
  }

  .nav-item-content {
    display: flex;
    align-items: center;
    height: 100%;
    width: 100%;
    transition: transform 0.3s ease;

    span {
      width: 130px;
      margin: 2px 10px 0;
    }
  }

  .has-no-items {
    span {
      width: 180px;
    }
  }

  .nav-arrow {
    width: 12px;
    transform: rotate(-90deg);
    -webkit-filter: grayscale(100%); /* Safari 6.0 - 9.0 */
    filter: invert(100%);
    opacity: 0.9;
  }

  .back-arrow {
    transform: rotate(90deg);
  }

  .nav-items::-webkit-scrollbar {
    width: 5px;
  }

  .nav-items::-webkit-scrollbar-thumb {
    box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
    background-color: #aaa;
    border-radius: 10px;
    position: absolute;
    left: 10px;
  }

  .nav-items {
    width: @side-nav-collapsed-width;
    display: flex;
    flex-direction: column;
    overflow-x: hidden;
    height: calc(100vh - 51px);
    transition: height 0.2s ease-in-out;

    .nav-item-content {
      height: @side-nav-item-height;
    }

    .settings {
      margin-top: auto;
    }
  }

  .nav-item-image-container {
    width: @side-nav-collapsed-width;
    display: flex;
    justify-content: center;
    align-items: center;

    .header-image {
      max-width: 40px;

      &[name="Home"],
      &[name="The Self-coaching Resources"],
      &[name="The Gyre Framework"] {
        max-width: 32px;
      }
    }

    .nav-item-image {
      -webkit-filter: grayscale(100%);
      filter: grayscale(100%);
      max-width: 60px;

      &[name="My Journey"],
      &[name="My Dashboard"] {
        max-width: 70px;
      }

      &[name="My Dashboard"] {
        margin-bottom: 10px;
      }

      &[name="My Journey"] {
        margin-bottom: 5px;
      }

      &[name="Setup a team"] {
        filter: invert(100%);
      }
    }

    .circle-image {
      border-radius: 50%;
    }

    .nav-item-bullet {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      border: 2px solid @side-nav-bullet-color;
    }
  }

  .is-active {
    .nav-item-image {
      -webkit-filter: initial;
      filter: initial;
    }
  }

  span,
  .nav-arrow,
  .nav-item-bullet {
    display: none;
  }

  .nav-item-head {
    width: 50px;
  }

  /* DIAMOND HEAD STYLES */

  .diamondRotate {
    a::before {
      border: none;
    }

    .btn-outline-innovator {
      background-color: #6dc8bf;
    }

    .btn-outline-entrepreneurial {
      background-color: #f8c31c;
    }

    .btn-outline-executional {
      background-color: #f3a123;
    }

    .btn-outline-practical {
      background-color: #c0c0c0;
    }

    .btn-outline-strategist {
      background-color: #823cbe;
    }

    .btn-outline-servant {
      background-color: #bf1224;
    }

    .btn-outline-transformational {
      background-color: #ed0b6f;
    }

    .btn-outline-charismatic {
      background-color: #b0bb36;
    }

    .btn-outline-professional {
      background-color: #67a6dc;
    }
  }
}

.nested-menu {
  .nav-item-content {
    visibility: hidden;

    .nav-item-image {
      max-width: 50px;
    }
  }

  .nav-header .nav-item-content {
    width: initial;
    transform: initial;
    margin-right: 10px;

    span {
      display: block;
      width: initial;
      max-width: 180px;
      margin: 2px 0 0;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    }

    .nav-item-image-container {
      max-width: 60px;
    }
  }
}
</style>

function $route($route: any, arg1: { immediate: true; deep: true; }) { throw new Error("Function not
implemented."); }
